import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  notificationApiRef,
  type Notification,
  type NotificationApi,
} from '../api/notificationApi';
import { NotificationErrorBoundary } from './ErrorBoundary';

type NotificationSignal = {
  action?: string;
  notification_id?: string;
  notification_ids?: string[];
};

// NOTE: We intentionally avoid getStatus() here even though it returns { unread: number }.
// getStatus() calls getNotificationsBaseQuery() without a `saved` filter, so it includes
// hidden (saved) items in the unread count. Using getNotifications with saved: false ensures
// the bell badge only reflects items that are actually visible to the user.
async function fetchUnreadCount(api: NotificationApi): Promise<number> {
  const response = await api.getNotifications({
    read: false,
    saved: false,
    limit: 1,
    offset: 0,
  });
  return response?.totalCount ?? 0;
}

export function NotificationBell() {
  const notificationsApi = useApi(notificationApiRef);
  const { lastSignal } = useSignal('notifications');

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const openRef = useRef(open);

  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadPreview, setUnreadPreview] = useState<Notification[]>([]);
  const [unreadTotal, setUnreadTotal] = useState(0);

  const refreshStatus = useCallback(async () => {
    try {
      setUnreadCount(await fetchUnreadCount(notificationsApi));
    } catch {
      // Keep previous count; UI should still work even if status fetch fails
    }
  }, [notificationsApi]);

  const refreshList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const unreadResponse = await notificationsApi.getNotifications({
        read: false,
        saved: false,
        limit: 3,
        offset: 0,
      });

      const unread = unreadResponse.notifications;
      const unreadCountFromApi = unreadResponse.totalCount;

      setUnreadPreview(unread.slice(0, 3));
      setUnreadTotal(
        typeof unreadCountFromApi === 'number'
          ? unreadCountFromApi
          : unread.length,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [notificationsApi]);

  // Debounce ref: coalesces rapid signals (e.g. bulk operations) into one list refresh
  const refreshListDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const debouncedRefreshList = useCallback(() => {
    if (refreshListDebounceRef.current)
      clearTimeout(refreshListDebounceRef.current);
    refreshListDebounceRef.current = setTimeout(() => refreshList(), 400);
  }, [refreshList]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    // Genuine fallback: only fires when the WebSocket signal connection is down.
    // useSignal handles reconnection, so 60 s is sufficient here.
    const statusInterval = window.setInterval(() => {
      refreshStatus();
    }, 60_000);

    return () => {
      window.clearInterval(statusInterval);
    };
  }, [refreshStatus]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const handleNewNotification = useCallback((notification: Notification) => {
    const n = notification;
    if (!n.read) {
      setUnreadTotal(prev => prev + 1);
      setUnreadPreview(prev => {
        if (prev.some(x => x.id === n.id)) return prev;
        if (prev.length >= 3) return prev;
        return [n, ...prev].slice(0, 3);
      });
    }
  }, []);

  const fetchAndHandleNew = useCallback(
    async (notificationId: string) => {
      try {
        const n = await notificationsApi.getNotification(notificationId);
        handleNewNotification(n);
      } catch {
        debouncedRefreshList();
      }
    },
    [notificationsApi, handleNewNotification, debouncedRefreshList],
  );

  useEffect(() => {
    if (!lastSignal?.action) return;
    const action = lastSignal.action;
    const signal = lastSignal as NotificationSignal;

    if (action === 'new_notification') {
      setUnreadCount(prev => prev + 1);
      if (openRef.current && signal.notification_id) {
        fetchAndHandleNew(signal.notification_id);
      }
      return;
    }

    if (action === 'notification_read') {
      const ids = signal.notification_ids ?? [];
      if (ids.length) {
        setUnreadCount(prev => Math.max(0, prev - ids.length));
        setUnreadPreview(prev => prev.filter(n => !ids.includes(n.id)));
        setUnreadTotal(prev => Math.max(0, prev - ids.length));
      }
      if (openRef.current) debouncedRefreshList();
      return;
    }

    if (action === 'notification_unread') {
      const ids = signal.notification_ids ?? [];
      if (ids.length) {
        setUnreadCount(prev => prev + ids.length);
      }
      if (openRef.current) debouncedRefreshList();
    }
  }, [lastSignal, debouncedRefreshList, fetchAndHandleNew]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    refreshList();
  }, [open, refreshList]);

  const listItems = useMemo(() => {
    const hasUnread = unreadTotal > 0;
    const unread = unreadPreview.slice(0, 3);
    const moreUnread = hasUnread ? Math.max(0, unreadTotal - unread.length) : 0;

    return { unread, moreUnread, hasUnread };
  }, [unreadPreview, unreadTotal]);

  const unreadBadgeContent = useMemo(() => {
    // Keep the badge circle from stretching (no oval). Clamp the label to 2 chars.
    const countLabel = unreadCount > 99 ? '99' : String(unreadCount);
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box component="span" sx={{ lineHeight: 1, fontSize: '0.75rem' }}>
          {countLabel}
        </Box>
      </Box>
    );
  }, [unreadCount]);

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={
          unreadCount
            ? `Open notifications, ${unreadCount} unread`
            : 'Open notifications'
        }
        onClick={handleOpen}
      >
        <Badge
          color="error"
          badgeContent={unreadBadgeContent}
          invisible={!unreadCount}
          sx={{
            '& .MuiBadge-badge': {
              width: 20,
              height: 20,
              minWidth: 20,
              padding: 0,
              borderRadius: '50%',
            },
          }}
        >
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 300,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: '480px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            My Communications
          </Typography>
        </Box>
        <Divider sx={{ flexShrink: 0 }} />

        <Box sx={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
          <NotificationErrorBoundary>
            {(() => {
              if (loading) {
                return (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                );
              }
              if (error) {
                return (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {error}
                    </Typography>
                  </Box>
                );
              }
              if (unreadPreview.length === 0) {
                return (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No unread notifications.
                    </Typography>
                  </Box>
                );
              }
              return (
                <List sx={{ py: 0 }}>
                  {listItems.unread.map((n, idx) => (
                    <Fragment key={n.id}>
                      {idx > 0 && <Divider />}
                      <ListItemButton
                        onClick={async () => {
                          handleClose();
                          if (!n.read) {
                            try {
                              await notificationsApi.updateNotifications({
                                ids: [n.id],
                                read: true,
                              });
                              refreshStatus();
                            } catch {
                              // Non-critical: navigate anyway; read state corrects on next load
                            }
                          }
                          navigate('/home/communications');
                        }}
                        sx={{
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: n.read ? 400 : 500,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            color: 'text.primary',
                          }}
                        >
                          {n.payload.title}
                        </Typography>
                      </ListItemButton>
                    </Fragment>
                  ))}

                  {listItems.moreUnread > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 2, py: 1, display: 'block' }}
                    >
                      and {listItems.moreUnread} more…
                    </Typography>
                  )}
                </List>
              );
            })()}
          </NotificationErrorBoundary>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <Button
            onClick={() => {
              handleClose();
              navigate('/home/communications');
            }}
            size="small"
            disableRipple
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
                color: 'text.primary',
              },
            }}
          >
            See more
          </Button>
        </Box>
      </Popover>
    </>
  );
}
